/* assets/novatto-mirror-addon.js
   Novatto – Matching Mirror Add-on (Product Page) – vNext+

   Adds:
   ✅ Header cart icon count bubble updates (Dawn-style)
   ✅ Mirror toggle thumbnail updates to match matched mirror VARIANT image
   ✅ Thumbnail crossfade on preview swaps (NEW)
   ✅ Selected ring hook (wrapper .is-selected) (NEW)

   Still:
   ✅ Consistent single-request add (vanity + mirror)
   ✅ Always shows feedback (toast + loading)
   ✅ Best-effort open cart drawer
*/

(function () {
  'use strict';

  const TOAST_MS = 2600;
  const DOUBLE_TAP_LOCK_MS = 1200;

  // -------------------------
  // Toast + button loading
  // -------------------------

  function ensureToastHost() {
    let host = document.querySelector('#nov-toast-host');
    if (host) return host;

    host = document.createElement('div');
    host.id = 'nov-toast-host';
    host.style.position = 'fixed';
    host.style.left = '50%';
    host.style.bottom = '18px';
    host.style.transform = 'translateX(-50%)';
    host.style.zIndex = '999999';
    host.style.pointerEvents = 'none';
    host.style.display = 'flex';
    host.style.flexDirection = 'column';
    host.style.gap = '10px';
    document.body.appendChild(host);
    return host;
  }

  function toast(msg, type) {
    const host = ensureToastHost();
    const el = document.createElement('div');
    el.className = 'nov-toast';
    el.textContent = msg;

    el.style.pointerEvents = 'none';
    el.style.maxWidth = 'min(92vw, 520px)';
    el.style.padding = '12px 14px';
    el.style.borderRadius = '999px';
    el.style.fontSize = '14px';
    el.style.fontWeight = '600';
    el.style.letterSpacing = '0.01em';
    el.style.boxShadow = '0 10px 22px rgba(0,0,0,0.18)';
    el.style.backdropFilter = 'blur(6px)';
    el.style.webkitBackdropFilter = 'blur(6px)';
    el.style.opacity = '0';
    el.style.transform = 'translateY(8px)';
    el.style.transition = 'opacity 160ms ease-out, transform 160ms ease-out';

    const isError = type === 'error';
    el.style.background = isError ? 'rgba(180, 30, 30, 0.92)' : 'rgba(4,16,73,0.92)';
    el.style.color = '#fff';

    host.appendChild(el);

    requestAnimationFrame(() => {
      el.style.opacity = '1';
      el.style.transform = 'translateY(0)';
    });

    window.setTimeout(() => {
      el.style.opacity = '0';
      el.style.transform = 'translateY(6px)';
      window.setTimeout(() => el.remove(), 220);
    }, TOAST_MS);
  }

  function setButtonLoading(form, isLoading) {
    const btn =
      form.querySelector('[type="submit"]') ||
      form.querySelector('button[name="add"]') ||
      form.querySelector('button');

    if (!btn) return;

    if (isLoading) {
      btn.__nov_prev_disabled = btn.disabled;
      btn.disabled = true;
      btn.setAttribute('aria-busy', 'true');
      btn.classList.add('is-loading');
    } else {
      btn.disabled = !!btn.__nov_prev_disabled ? true : false;
      btn.removeAttribute('aria-busy');
      btn.classList.remove('is-loading');
      btn.__nov_prev_disabled = undefined;
    }
  }

  // -------------------------
  // Cart + header bubble helpers
  // -------------------------

  function getCart() {
    return fetch('/cart.js', { credentials: 'same-origin' }).then(r => r.json());
  }

  function updateHeaderCartCount(cart) {
    try {
      const count = Number(cart?.item_count || 0);

      // Dawn header anchor is usually #cart-icon-bubble
      const cartLink =
        document.querySelector('#cart-icon-bubble') ||
        document.querySelector('a[href="/cart"]') ||
        document.querySelector('a[href^="/cart"]');

      if (!cartLink) return;

      // Dawn bubble wrapper
      let bubble = cartLink.querySelector('.cart-count-bubble');

      // Remove bubble if empty
      if (count <= 0) {
        if (bubble) bubble.remove();
        return;
      }

      // Create bubble if missing (match Dawn-ish markup)
      if (!bubble) {
        bubble = document.createElement('div');
        bubble.className = 'cart-count-bubble';

        const spanVisible = document.createElement('span');
        spanVisible.setAttribute('aria-hidden', 'true');

        const spanHidden = document.createElement('span');
        spanHidden.className = 'visually-hidden';

        bubble.appendChild(spanVisible);
        bubble.appendChild(spanHidden);

        // Put it inside the cart icon link (Dawn does this)
        cartLink.appendChild(bubble);
      }

      const visible = bubble.querySelector('span[aria-hidden="true"]');
      const hidden = bubble.querySelector('.visually-hidden');

      if (visible) visible.textContent = String(count);
      if (hidden) hidden.textContent = count === 1 ? '1 item' : `${count} items`;
    } catch (e) {
      // no-op
    }
  }

  function cartChange(line, body) {
    return fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify(Object.assign({ line }, body))
    }).then((r) => r.json());
  }

  function getProp(item, name) {
    const p = item && item.properties;
    if (!p) return null;
    const v = p[name];
    return typeof v === 'string' ? v : (v != null ? String(v) : null);
  }

  function notifyCartUpdated() {
    document.dispatchEvent(new CustomEvent('cart:update'));
    document.dispatchEvent(new CustomEvent('cart:refresh'));
    document.dispatchEvent(new CustomEvent('novatto:cart:add'));
  }

  function tryOpenCartDrawer() {
    const drawer = document.querySelector('cart-drawer');
    if (drawer && typeof drawer.open === 'function') {
      try { drawer.open(); return true; } catch (_) {}
    }

    const trigger =
      document.querySelector('[aria-controls="CartDrawer"]') ||
      document.querySelector('[data-drawer-trigger]');

    if (trigger && typeof trigger.click === 'function') {
      try { trigger.click(); return true; } catch (_) {}
    }

    return false;
  }

  // -------------------------
  // JSON + parsing helpers
  // -------------------------

  function safeJsonParse(text) {
    try { return JSON.parse(text); } catch (_) { return null; }
  }

  function getProductForm(scope) {
    return (
      scope.querySelector('product-form form[action*="/cart/add"]') ||
      scope.querySelector('form[action*="/cart/add"]')
    );
  }

  function getSelectedVariantIdFromForm(form) {
    const idInput = form ? form.querySelector('input[name="id"]') : null;
    const id = idInput ? parseInt(idInput.value, 10) : NaN;
    return Number.isFinite(id) ? id : null;
  }

  function getSelectedQuantityFromForm(form) {
    if (!form) return 1;

    // 1) If quantity input is inside the form (rare on Dawn)
    let qtyInput = form.querySelector('input[name="quantity"]');

    // 2) Dawn: quantity input is often OUTSIDE the form but linked via form="..."
    if (!qtyInput) {
      const formId = form.getAttribute('id');
      if (formId) {
        // try within the closest product-info first (safer than whole document)
        const scope = form.closest('product-info') || document;

        // CSS.escape is not supported in older browsers, so we do a safe fallback
        const safeId = (window.CSS && CSS.escape) ? CSS.escape(formId) : formId.replace(/"/g, '\\"');

        qtyInput = scope.querySelector(`input[name="quantity"][form="${safeId}"]`);
      }
    }

    // 3) Last-resort: find any quantity-input nearest the form
    if (!qtyInput) {
      const scope = form.closest('product-info') || document;
      qtyInput = scope.querySelector('quantity-input input[name="quantity"]');
    }

    const parsed = qtyInput ? parseInt(qtyInput.value, 10) : NaN;
    return Number.isFinite(parsed) && parsed > 0 ? parsed : 1;
  }

  function getFinishFamilyFromWoodValue(rawValue) {
    if (!rawValue) return null;

    let code = String(rawValue);
    const parts = code.split('(');
    if (parts.length > 1) {
      code = parts[parts.length - 1].replace(')', '').trim();
    }

    if (!code) return null;

    const family = code.split('-')[0];
    return family ? String(family).toUpperCase().trim() : null;
  }

  function getSelectedWoodOptionValue(productForm, productData, woodOptionName) {
    if (!productForm || !productData || !Array.isArray(productData.variants)) return null;

    const currentId = getSelectedVariantIdFromForm(productForm);
    if (!currentId) return null;

    const v = productData.variants.find(x => parseInt(x.id, 10) === currentId);
    if (!v || !Array.isArray(v.options)) return null;

    const optionNames = Array.isArray(productData.options) ? productData.options : [];
    const idx = optionNames.findIndex(name => (name || '').trim().toLowerCase() === String(woodOptionName || '').toLowerCase());
    if (idx === -1) return null;

    return (v.options[idx] || '').trim() || null;
  }

  function findMirrorVariantForFamily(mirrorProduct, family) {
    if (!mirrorProduct || !family) return null;
    const variants = mirrorProduct.variants || [];
    const fam = String(family).toUpperCase().trim();

    for (const v of variants) {
      const sku = (v.sku || '').toUpperCase().trim();
      if (sku.length >= 2 && sku.slice(-2) === fam) return v;
    }
    return null;
  }

  function extractLineItemProperties(form) {
    const props = {};
    try {
      const fd = new FormData(form);
      for (const [key, value] of fd.entries()) {
        const m = String(key).match(/^properties\[(.+)\]$/);
        if (m && m[1]) props[m[1]] = String(value);
      }
    } catch (_) {}
    return props;
  }

  function setWarning(warningEl, msg) {
    if (!warningEl) return;
    warningEl.textContent = msg || '';
    warningEl.hidden = !msg;
  }

  // -------------------------
  // ✅ Thumbnail switching (variant image)
  // -------------------------

  function normalizeShopifyCdnUrl(src) {
    if (!src) return null;
    let s = String(src);

    // JSON can include "///" escapes sometimes; just normalize common patterns
    s = s.replace(/\\\//g, '/');

    // Make sure it's protocol-relative or absolute
    if (s.startsWith('//')) return s;
    if (s.startsWith('http://') || s.startsWith('https://')) return s;
    if (s.startsWith('/')) return s; // already path
    return '//' + s;
  }

  function withWidthParam(url, width) {
    if (!url) return url;
    const u = String(url);
    // If Shopify CDN image, you can usually append &width= or ?width=
    if (u.includes('?')) return u + `&width=${width}`;
    return u + `?width=${width}`;
  }

  function getVariantImageUrl(mirrorVariant) {
    // Variant can include featured_image.src in product JSON
    const src =
      mirrorVariant?.featured_image?.src ||
      mirrorVariant?.featured_media?.preview_image?.src ||
      null;

    const normalized = normalizeShopifyCdnUrl(src);
    return normalized ? withWidthParam(normalized, 240) : null;
  }

  // -------------------------
  // Per-product init (preview only)
  // -------------------------

  function initOne(scope) {
    const configEl  = scope.querySelector('[data-nov-mirror-config]');
    const wrapperEl = scope.querySelector('[data-nov-mirror-wrapper]');
    const toggleEl  = scope.querySelector('[data-nov-mirror-toggle]');
    if (!configEl || !wrapperEl || !toggleEl) return;

    const config = safeJsonParse(configEl.textContent);
    if (!config) return;

    const productForm = getProductForm(scope);
    if (!productForm) return;

    if (scope.__nov_mirror_init_done) return;
    scope.__nov_mirror_init_done = true;

    const mirrorProduct = config.mirrorProduct;
    const productData   = config.productData;
    const woodOptionName = (config.woodOptionName || 'Wood and Handle Finish').trim();

    const previewTextEl  = wrapperEl.querySelector('[data-nov-mirror-preview-text]');
    const badgeEl        = wrapperEl.querySelector('[data-nov-mirror-badge]');
    const warningEl      = wrapperEl.querySelector('[data-nov-mirror-warning]');
    const thumbEl        = wrapperEl.querySelector('[data-nov-mirror-thumb]');

    // -------------------------
    // Product price display with mirror add-on
    // -------------------------

    function formatMoney(cents) {
      const moneyFormat = config.moneyFormat || '${{amount}}';

      if (typeof Shopify !== 'undefined' && Shopify.formatMoney) {
        return Shopify.formatMoney(cents, moneyFormat);
      }

      return '$' + (Number(cents || 0) / 100).toFixed(2);
    }

    function getSelectedVanityVariant() {
      if (!productData || !Array.isArray(productData.variants)) return null;

      const currentId = getSelectedVariantIdFromForm(productForm);
      if (!currentId) return null;

      return productData.variants.find(function(variant) {
        return Number(variant.id) === Number(currentId);
      }) || null;
    }

    function getMainProductPriceRoot() {
      const roots = Array.from(scope.querySelectorAll('.price'));

      return roots.find(function(root) {
        return (
          root &&
          !root.closest('[data-nov-mirror-wrapper]') &&
          !root.closest('cart-drawer') &&
          !root.closest('cart-items')
        );
      }) || null;
    }

    function updateDisplayedProductPrice(mirrorVariant) {
      const priceRoot = getMainProductPriceRoot();
      const vanityVariant = getSelectedVanityVariant();

      if (!priceRoot || !vanityVariant) return;

      const mirrorIsIncluded = !!toggleEl.checked && !!mirrorVariant;
      const vanityPrice = Number(vanityVariant.price || 0);
      const mirrorPrice = mirrorIsIncluded ? Number(mirrorVariant.price || 0) : 0;

      const totalPrice = vanityPrice + mirrorPrice;

      const vanityCompareAt = Number(vanityVariant.compare_at_price || 0);
      const vanityIsOnSale = vanityCompareAt > vanityPrice;

      const mirrorCompareAt = mirrorIsIncluded
        ? Number(mirrorVariant.compare_at_price || mirrorVariant.price || 0)
        : 0;

      const totalCompareAt = vanityIsOnSale
        ? vanityCompareAt + mirrorCompareAt
        : 0;

      const formattedTotal = formatMoney(totalPrice);
      const formattedCompareAt = formatMoney(totalCompareAt);

      const regularWrap = priceRoot.querySelector('.price__regular');
      const saleWrap = priceRoot.querySelector('.price__sale');

      const regularPriceItem = regularWrap
        ? regularWrap.querySelector('.price-item--regular')
        : null;

      const salePriceItem = saleWrap
        ? saleWrap.querySelector('.price-item--sale')
        : null;

      const compareAtItem = saleWrap
        ? saleWrap.querySelector('.price-item--regular')
        : null;

      if (vanityIsOnSale && salePriceItem) {
        priceRoot.classList.add('price--on-sale');

        salePriceItem.textContent = formattedTotal;

        if (compareAtItem) {
          compareAtItem.textContent = formattedCompareAt;
        }

        if (regularPriceItem) {
          regularPriceItem.textContent = formattedTotal;
        }
      } else {
        priceRoot.classList.remove('price--on-sale');

        if (regularPriceItem) {
          regularPriceItem.textContent = formattedTotal;
        }

        if (salePriceItem) {
          salePriceItem.textContent = formattedTotal;
        }

        if (compareAtItem) {
          compareAtItem.textContent = '';
        }
      }

      priceRoot.classList.toggle('nov-price-includes-mirror', mirrorIsIncluded);
    }

    // ✅ NEW: Selected ring hook
    function setSelectedUI(isOn) {
      wrapperEl.classList.toggle('is-selected', !!isOn);
    }

    function ensureThumbCrossfadeStyles(img) {
      if (!img) return;
      // Ensure we can layer an overlay on top of the img
      const parent = img.parentElement;
      if (!parent) return;

      const parentStyle = window.getComputedStyle(parent);
      if (parentStyle.position === 'static') parent.style.position = 'relative';

      // Make sure the base img participates nicely
      img.style.display = 'block';
      img.style.width = img.style.width || '100%';
      img.style.height = img.style.height || 'auto';
    }

    function crossfadeThumbTo(src, altText) {
      if (!thumbEl || !src) return;

      const current = thumbEl.getAttribute('src') || '';
      if (current === src) {
        if (altText) thumbEl.alt = altText;
        return;
      }

      ensureThumbCrossfadeStyles(thumbEl);

      const parent = thumbEl.parentElement;
      if (!parent) {
        // Fallback: just swap
        if (altText) thumbEl.alt = altText;
        thumbEl.src = src;
        return;
      }

      // Remove any prior overlay
      const existingOverlay = parent.querySelector('.nov-thumb-xfade-overlay');
      if (existingOverlay) existingOverlay.remove();

      // Create overlay clone showing the "old" image
      const overlay = thumbEl.cloneNode(true);
      overlay.classList.add('nov-thumb-xfade-overlay');
      overlay.removeAttribute('data-nov-mirror-thumb'); // avoid accidental re-query issues

      // Force overlay positioning on top
      overlay.style.position = 'absolute';
      overlay.style.inset = '0';
      overlay.style.width = '100%';
      overlay.style.height = '100%';
      overlay.style.objectFit = window.getComputedStyle(thumbEl).objectFit || 'cover';
      overlay.style.pointerEvents = 'none';
      overlay.style.opacity = '1';
      overlay.style.transition = 'opacity 240ms ease-out';

      // If the base image isn't explicitly sized, overlay needs same layout box:
      // Ensure parent has a real height from the base image layout
      parent.appendChild(overlay);

      // Prepare base image swap
      if (altText) thumbEl.alt = altText;

      // When new image is ready, fade overlay out
      const finish = () => {
        // Fade overlay away
        requestAnimationFrame(() => {
          overlay.style.opacity = '0';
        });
        window.setTimeout(() => {
          overlay.remove();
        }, 260);
      };

      // Attach load/error BEFORE setting src
      const onLoad = () => {
        thumbEl.removeEventListener('load', onLoad);
        thumbEl.removeEventListener('error', onLoad);
        finish();
      };
      thumbEl.addEventListener('load', onLoad);
      thumbEl.addEventListener('error', onLoad);

      // Swap src (next frame helps ensure overlay is painted first)
      requestAnimationFrame(() => {
        // Once JS controls the matching variant image, remove the original
        // product-level responsive candidates so the browser uses the
        // matched variant image instead.
        thumbEl.removeAttribute('srcset');
        thumbEl.removeAttribute('sizes');

        thumbEl.src = src;

        // Cached-image safety: if it’s already complete, resolve immediately.
        if (thumbEl.complete) {
          thumbEl.removeEventListener('load', onLoad);
          thumbEl.removeEventListener('error', onLoad);
          finish();
        }
      });
    }

    function clearBadgeAndPrice() {
      if (badgeEl) {
        badgeEl.textContent = '';
        badgeEl.classList.remove('is-visible');
      }
    }

    function updateMirrorPreview() {
      if (!previewTextEl) return;

      setWarning(warningEl, '');
      clearBadgeAndPrice();

      const woodValue = getSelectedWoodOptionValue(productForm, productData, woodOptionName);
      const family = getFinishFamilyFromWoodValue(woodValue);
      const mirrorVariant = family ? findMirrorVariantForFamily(mirrorProduct, family) : null;

      if (!woodValue || !family) {
        previewTextEl.textContent = 'Select a wood finish to see the matching mirror.';
        updateDisplayedProductPrice(null);
        return;
      }

      if (!mirrorVariant) {
        previewTextEl.textContent = 'Matching mirror unavailable for this finish.';
        setWarning(warningEl, 'Matching mirror is not available for this finish yet.');
        updateDisplayedProductPrice(null);
        return;
      }

      previewTextEl.textContent = mirrorVariant.title || mirrorProduct.title || 'Matching mirror';

      // Update thumbnail to matched variant image with crossfade.
      if (thumbEl) {
        const variantImg = getVariantImageUrl(mirrorVariant);

        if (variantImg) {
          const altText =
            mirrorVariant.name ||
            mirrorVariant.title ||
            mirrorProduct.title ||
            'Matching mirror';

          crossfadeThumbTo(variantImg, altText);
        }
      }

      // Mirror add-on price UI.
      if (typeof mirrorVariant.price === 'number' && badgeEl) {
        const formatted = formatMoney(mirrorVariant.price);

        badgeEl.textContent = '+ ' + formatted;
        badgeEl.classList.add('is-visible');
      }

      // Main Dawn product price.
      updateDisplayedProductPrice(mirrorVariant);
    }

    function schedulePreview() {
      requestAnimationFrame(() => requestAnimationFrame(updateMirrorPreview));
    }

    // ✅ Ensure ring state is correct immediately
    setSelectedUI(!!toggleEl.checked);

    schedulePreview();

    productForm.addEventListener('change', (e) => {
      const t = e.target;
      if (!t) return;
      if (t.matches('input[type="radio"], select')) schedulePreview();
    });

    // ✅ Keep your schedulePreview, but also update selected ring
    toggleEl.addEventListener('change', () => {
      setSelectedUI(!!toggleEl.checked);
      schedulePreview();
    });

    const idInput = productForm.querySelector('input[name="id"]');
    if (idInput) {
      const mo = new MutationObserver(schedulePreview);
      mo.observe(idInput, { attributes: true, attributeFilter: ['value'] });

      let last = idInput.value;
      setInterval(() => {
        if (idInput.value !== last) {
          last = idInput.value;
          schedulePreview();
        }
      }, 250);
    }
  }

  function initAll() {
    document.querySelectorAll('product-info').forEach(initOne);
  }

  document.addEventListener('DOMContentLoaded', initAll);
  document.addEventListener('shopify:section:load', initAll);

  // -------------------------
  // ✅ Submit intercept (capture)
  // -------------------------

  let lastSubmitAt = 0;

  document.addEventListener('submit', async function (e) {
    const form = e.target;
    if (!(form instanceof HTMLFormElement)) return;
    if (!form.action || !form.action.includes('/cart/add')) return;

    const scope = form.closest('product-info') || document;

    const toggleEl = scope.querySelector('[data-nov-mirror-toggle]');
    const configEl = scope.querySelector('[data-nov-mirror-config]');
    const wrapperEl = scope.querySelector('[data-nov-mirror-wrapper]');
    const warningEl = wrapperEl ? wrapperEl.querySelector('[data-nov-mirror-warning]') : null;

    // Only intercept when mirror UI exists AND toggle is checked
    if (!toggleEl || !configEl || !wrapperEl) return;
    if (!toggleEl.checked) return;

    const now = Date.now();
    if (now - lastSubmitAt < DOUBLE_TAP_LOCK_MS) {
      e.preventDefault();
      e.stopPropagation();
      if (e.stopImmediatePropagation) e.stopImmediatePropagation();
      return;
    }
    lastSubmitAt = now;

    const config = safeJsonParse(configEl.textContent);
    if (!config) return;

    const productHandle = (config.productHandle || '').trim();
    const mirrorProduct = config.mirrorProduct;
    const productData   = config.productData;
    const woodOptionName = (config.woodOptionName || 'Wood and Handle Finish').trim();

    // Stop Dawn flow
    e.preventDefault();
    e.stopPropagation();
    if (e.stopImmediatePropagation) e.stopImmediatePropagation();

    if (form.__nov_mirror_inflight) return;
    form.__nov_mirror_inflight = true;

    setWarning(warningEl, '');
    setButtonLoading(form, true);

    try {
      const vanityVariantId = getSelectedVariantIdFromForm(form);
      const qty = getSelectedQuantityFromForm(form);

      const woodValue = getSelectedWoodOptionValue(form, productData, woodOptionName);
      const family = getFinishFamilyFromWoodValue(woodValue);
      const mirrorVariant = family ? findMirrorVariantForFamily(mirrorProduct, family) : null;

      console.log('[Novatto mirror] submit snapshot', {
        variantId: vanityVariantId,
        vanitySku: (productData?.variants || []).find(v => Number(v.id) === Number(vanityVariantId))?.sku,
        woodOptionName,
        woodValue,
        family,
        mirrorMatchSku: mirrorVariant?.sku,
        mirrorVariantId: mirrorVariant?.id
      });

      if (!vanityVariantId) {
        setWarning(warningEl, 'Please select all options before adding to cart.');
        toast('Please select all options first.', 'error');
        return;
      }

      if (!woodValue || !family) {
        setWarning(warningEl, 'Please select a wood finish to add a matching mirror.');
        toast('Select a wood finish for the matching mirror.', 'error');
        return;
      }

      if (!mirrorVariant || !mirrorVariant.id) {
        setWarning(warningEl, 'Matching mirror is not available for this finish yet.');
        toast('Matching mirror unavailable for this finish.', 'error');
        return;
      }

      if (mirrorVariant.available === false) {
        setWarning(warningEl, 'Matching mirror is currently sold out for this finish.');
        toast('Matching mirror is sold out for this finish.', 'error');
        return;
      }

      const baseProps = extractLineItemProperties(form);

      // ✅ Shared bundle id ties the two line items together reliably
      const bundleId =
        (window.crypto && crypto.randomUUID && crypto.randomUUID()) ||
        `nov_${Date.now()}_${Math.random().toString(16).slice(2)}`;

      const parentProps = Object.assign({}, baseProps, {
        _nov_bundle_id: bundleId,
        _nov_bundle_role: 'parent'
      });

      const mirrorProps = Object.assign({}, baseProps, {
        _nov_bundle_id: bundleId,
        _nov_bundle_role: 'mirror',

        // Keep your original link (fallback)
        _matching_mirror_for: productHandle || ''
      });

      const payload = {
        items: [
          { id: vanityVariantId, quantity: qty, properties: parentProps },
          { id: mirrorVariant.id, quantity: qty, properties: mirrorProps }
        ]
      };

      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify(payload)
      });

      if (!res.ok) {
        let errPayload = null;
        try { errPayload = await res.json(); } catch (_) {}

        const msg = (errPayload && (errPayload.description || errPayload.message)) || 'Could not add matching mirror.';
        setWarning(warningEl, msg);
        toast(msg, 'error');
        console.warn('[Novatto mirror] /cart/add.js failed', { status: res.status, errPayload, payload });
        return;
      }

      // ✅ Always show feedback
      toast('Added to cart.', 'success');

      // ✅ Stamp mirror with the parent cart key (strongest link)
      try {
        const cart = await getCart();
        const hits = (cart.items || []).filter((it) => getProp(it, '_nov_bundle_id') === bundleId);

        const parentLine = hits.find((it) => getProp(it, '_nov_bundle_role') === 'parent');
        const mirrorLine = hits.find((it) => getProp(it, '_nov_bundle_role') === 'mirror');

        if (parentLine && mirrorLine && parentLine.key) {
          const nextMirrorProps = Object.assign({}, mirrorLine.properties || {}, {
            _nov_parent_key: parentLine.key,
            _nov_parent_variant_id: String(parentLine.variant_id || '')
          });

          // Shopify requires full properties object when updating
          await cartChange(mirrorLine.index + 1, {
            quantity: mirrorLine.quantity,
            properties: nextMirrorProps
          });
        }

        // Refresh bubble using the newest cart
        updateHeaderCartCount(cart);
      } catch (_) {
        // best-effort only
      }

      // ✅ Update header cart bubble immediately
      try {
        const cart = await getCart();
        updateHeaderCartCount(cart);
      } catch (_) {}

      // ✅ Let other listeners refresh
      notifyCartUpdated();

      // ✅ Best-effort open cart drawer
      tryOpenCartDrawer();

    } catch (err) {
      console.error('[Novatto mirror] submit error', err);
      toast('Could not add to cart (error).', 'error');
      setWarning(warningEl, 'Could not add to cart (unexpected error).');
    } finally {
      form.__nov_mirror_inflight = false;
      setButtonLoading(form, false);
    }
  }, true);
})();

(function () {
  function syncQuantityMinusState(scope) {
    const inputs = scope.querySelectorAll('quantity-input input.quantity__input');

    inputs.forEach(input => {
      const min = parseInt(input.getAttribute('min') || input.dataset.min || 1, 10);
      const val = parseInt(input.value || min, 10);

      const minusBtn = input.closest('quantity-input')?.querySelector('button[name="minus"]');
      if (!minusBtn) return;

      if (val > min) {
        minusBtn.disabled = false;
        minusBtn.removeAttribute('aria-disabled');
        minusBtn.classList.remove('is-disabled');
      } else {
        minusBtn.disabled = true;
        minusBtn.setAttribute('aria-disabled', 'true');
        minusBtn.classList.add('is-disabled');
      }
    });
  }

  function init(scope = document) {
    syncQuantityMinusState(scope);

    scope.addEventListener('click', e => {
      if (e.target.closest('quantity-input button')) {
        requestAnimationFrame(() => syncQuantityMinusState(scope));
      }
    });

    scope.addEventListener('change', e => {
      if (e.target.matches('quantity-input input.quantity__input')) {
        syncQuantityMinusState(scope);
      }
    });
  }

  document.addEventListener('DOMContentLoaded', () => init(document));
  document.addEventListener('shopify:section:load', e => init(e.target));
})();
